import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Type,
  Send,
  Trash2,
  Eye,
  Settings,
  Layers,
} from "lucide-react";

export default function CustomLetter() {
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);

  const [sentLetters, setSentLetters] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sentCustomLetters") || "[]");
    } catch {
      return [];
    }
  });

  const [form, setForm] = useState({
    letterTitle: "",
    employeeName: "",
    employeeEmail: "",
    subject: "",
    salutation: "Dear",
    content: "",
    closing: "Sincerely,",
    issuedBy: "",
  });

  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSend = () => {
    if (!form.letterTitle || !form.content) {
      alert("Letter title and body are required");
      return;
    }

    const newLetter = {
      ...form,
      id: Date.now(),
      sentAt: new Date().toLocaleString(),
    };

    const updated = [newLetter, ...sentLetters];
    setSentLetters(updated);
    localStorage.setItem("sentCustomLetters", JSON.stringify(updated));
    setShowPreview(false);
    alert(`Custom letter "${form.letterTitle}" saved and issued.`);
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-8" style={robotoStyle}>
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-slate-500 hover:text-blue-600 mb-2 transition-colors text-sm font-medium"
            >
              <ArrowLeft size={16} /> Back
            </button>
            <h1
              className="text-2xl font-semibold text-slate-900"
              style={poppinsStyle}
            >
              Custom Letter Builder
            </h1>
            <p className="text-sm text-slate-500">
              Compose personalized documents with a custom template.
            </p>
          </div>
          <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
            <Settings size={24} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* ================= EDITOR ================= */}
          <div className="lg:col-span-7 space-y-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-6 pb-4 border-b">
                <Type className="text-blue-500" size={18} />
                <h2
                  className="text-sm font-bold uppercase tracking-widest text-slate-800"
                  style={poppinsStyle}
                >
                  Composer
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <Input
                    label="Letter Title"
                    placeholder="e.g. Promotion Letter"
                    value={form.letterTitle}
                    onChange={(v) => handleChange("letterTitle", v)}
                  />
                </div>

                <Input
                  label="Employee Name"
                  value={form.employeeName}
                  onChange={(v) => handleChange("employeeName", v)}
                />

                <Input
                  label="Recipient Email"
                  type="email"
                  value={form.employeeEmail}
                  onChange={(v) => handleChange("employeeEmail", v)}
                />

                <div className="md:col-span-2">
                  <Input
                    label="Subject Line"
                    placeholder="Regarding your recent..."
                    value={form.subject}
                    onChange={(v) => handleChange("subject", v)}
                  />
                </div>
              </div>

              <div className="mt-5 space-y-4">
                <div className="flex gap-4">
                  <div className="w-1/3">
                    <Input
                      label="Salutation"
                      value={form.salutation}
                      onChange={(v) => handleChange("salutation", v)}
                    />
                  </div>
                  <div className="w-2/3">
                    <Input
                      label="Issued By"
                      value={form.issuedBy}
                      onChange={(v) => handleChange("issuedBy", v)}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                    Letter Body
                  </label>
                  <textarea
                    className="w-full border border-slate-200 rounded-xl px-4 py-3 h-64 bg-slate-50/30 focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all text-sm leading-relaxed"
                    placeholder="Type your official message here..."
                    value={form.content}
                    onChange={(e) =>
                      handleChange("content", e.target.value)
                    }
                  />
                </div>

                <div className="w-1/3">
                  <Input
                    label="Closing Statement"
                    value={form.closing}
                    onChange={(v) => handleChange("closing", v)}
                  />
                </div>
              </div>

              <div className="mt-8">
                <button
                  onClick={() => setShowPreview(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 rounded-xl font-semibold flex items-center gap-2 transition-all shadow-lg shadow-blue-200"
                >
                  <Eye size={18} /> Preview Document
                </button>
              </div>
            </div>
          </div>

          {/* ================= PREVIEW ================= */}
          <div className="lg:col-span-5 space-y-6">
            <div
              className={`bg-white rounded-2xl border-2 ${
                showPreview
                  ? "border-blue-500 shadow-xl"
                  : "border-slate-100 opacity-60"
              } p-8 transition-all duration-300 sticky top-8 overflow-hidden`}
            >
              <div className="flex items-center justify-between mb-8">
                <Layers className="text-slate-300" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  Official Preview
                </span>
              </div>

              {/* Letter Page */}
              <div className="mx-auto max-w-[800px] border border-slate-200 rounded-lg p-10 shadow-sm bg-white">
                <div className="space-y-5 text-slate-800 text-sm leading-relaxed break-words">

                  <h3 className="text-center font-bold text-lg mb-10 underline decoration-blue-100 underline-offset-8">
                    {form.letterTitle || "LETTER TITLE"}
                  </h3>

                  <p className="text-slate-500 text-xs italic">
                    Date: {new Date().toLocaleDateString()}
                  </p>

                  <p>
                    {form.salutation}{" "}
                    <strong>
                      {form.employeeName || "[Employee Name]"}
                    </strong>
                    ,
                  </p>

                  {form.subject && (
                    <p className="font-bold break-words">
                      Sub: {form.subject}
                    </p>
                  )}

                  <p className="whitespace-pre-wrap break-words overflow-hidden">
                    {form.content ||
                      "Start typing in the composer to see your letter content appear here..."}
                  </p>

                  <div className="pt-10">
                    <p>{form.closing}</p>
                    <p className="font-bold mt-1">
                      {form.issuedBy || "[Authorized Signatory]"}
                    </p>
                  </div>
                </div>
              </div>

              {showPreview && (
                <div className="mt-8 pt-6 border-t flex flex-col gap-3">
                  <button
                    onClick={handleSend}
                    className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-blue-600 transition-all shadow-md flex items-center justify-center gap-2"
                  >
                    <Send size={16} /> Save & Issue
                  </button>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="text-slate-400 hover:text-slate-600 text-[10px] font-bold uppercase tracking-widest"
                  >
                    Edit Draft
                  </button>
                </div>
              )}
            </div>

            {/* History */}
            {sentLetters.length > 0 && !showPreview && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3
                  className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4"
                  style={poppinsStyle}
                >
                  Previously Issued
                </h3>

                <div className="space-y-3">
                  {sentLetters.slice(0, 3).map((l) => (
                    <div
                      key={l.id}
                      className="flex justify-between items-center p-3 rounded-lg hover:bg-slate-50 group"
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-700">
                          {l.letterTitle}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {l.employeeName} • {l.sentAt.split(",")[0]}
                        </p>
                      </div>
                      <button
                        onClick={() =>
                          setSentLetters((prev) =>
                            prev.filter((x) => x.id !== l.id)
                          )
                        }
                        className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= HELPER INPUT ================= */

function Input({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
}) {
  return (
    <div className="space-y-1.5 w-full">
      <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
        {label}
      </label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/30 focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
      />
    </div>
  );
}
